import { useIsFocused, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useMutation } from "@tanstack/react-query";
import FullScreenSpinner from "components/FullScreenSpinner";
import MainContainer from "components/MainContainer";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import { resetAddMembershipFlow } from "features/slices/addMembershipFlowSlice";
import {
  fetchAddMemberPlanPackages,
  fetchSignupCharge,
  resetBuyFlow,
} from "features/slices/buyFlowSlice";
import { AccountDetails, BuyFlowUser } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import useTenantUserFields from "hooks/useTenantUserFields";
import { AccountStackParamList } from "navigation/types";
import { addMembersFetcher } from "queries/account.queries";
import React, { useEffect, useMemo } from "react";
import { View } from "react-native";
import { planPackageIdArray } from "screens/Register/registerUtils";
import fileServices from "services/file.services";
import { useDebouncedCallback } from "use-debounce";
import toast from "utils/toast";
import { convertUserToUserFields } from "utils/userUtils";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import StepTwo from "./StepTwo";

const AddMembership = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AccountStackParamList>>();
  const { account, updateFetchAccountBffCache } = useAccount();

  const isFocused = useIsFocused();
  const dispatch = useAppDispatch();
  const {
    planPackages,
    step,
    signupCharge,
    selectedBillingInterval,
    selectedPlanPackage,
  } = useAppSelector((state) => state.buyFlow);
  const { newMembers, skipAddingPlans } = useAppSelector(
    (state) => state.addMembershipFlow
  );
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id
  );

  const sendConversionData = useSendConversionData();
  const { mutateAsync: addMembers, isLoading: loading } =
    useMutation(addMembersFetcher);

  const goBack = () => {
    if (!isFocused) return;
    navigation.replace("ManageSubscriptions", {
      screen: "ManageSubscriptionsScreen",
    });
  };

  const initPlanPackages = async (account: AccountDetails) => {
    dispatch(fetchAddMemberPlanPackages(account));
  };

  const refetchSignupCharge = useDebouncedCallback((selectedPlanPackage) => {
    if (newMembers.length > 0 && !skipAddingPlans) {
      dispatch(
        fetchSignupCharge({
          planPackageIds: planPackageIdArray(
            newMembers.length,
            selectedPlanPackage.id
          ),
          billingInterval: selectedBillingInterval,
        })
      );
    }
  }, 250);

  const getImageIdentifier = async (imageIdentifier: string) => {
    if (imageIdentifier === "") return "";
    const response = await fileServices.uploadImage(imageIdentifier);
    return response.value;
  };

  const uploadImage = async (membersParam: any) => {
    for (let member of membersParam) {
      member.imageIdentifier = await getImageIdentifier(member.imageIdentifier);
    }
  };

  const addMemberToAccount = async (members: BuyFlowUser[]) => {
    const planPackageIds: number[] = [];
    if (!skipAddingPlans) {
      planPackageIds.push(selectedPlanPackage!.id);
    }

    const membersParam = members.map((member) => {
      const param = {
        ...member,
        planPackageIds,
        imageIdentifier: member.imageIdentifier,
        userFields: {
          ...convertUserToUserFields({
            user: member,
            tenantOrPlanPackageUserFields: tenantUserFields!,
          }),
        },
        type: "SECONDARY",
      };
      if (!skipAddingPlans && selectedPlanPackage) {
        param.userFields = {
          ...param.userFields,
          ...convertUserToUserFields({
            user: member,
            tenantOrPlanPackageUserFields: planPackageUserFields!,
          }),
        };
      }
      return param;
    });
    // retreive image identifier for each member
    await uploadImage(membersParam);
    return await addMembers({
      billingInterval: selectedBillingInterval,
      members: membersParam as any,
      accountId: account!.id,
      source: "FANRALLY_APP",
    });
  };

  const handleSubmit = async () => {
    const totalQuantity = newMembers.length;
    const updatedAccount = await addMemberToAccount([...newMembers]);
    goBack();
    updateFetchAccountBffCache(updatedAccount);
    dispatch(resetBuyFlow());
    dispatch(resetAddMembershipFlow());
    toast.success("New member added successfully.");
    sendConversionData(signupCharge, totalQuantity);
  };

  const availableSteps = useMemo(() => {
    const steps = [
      { name: "Choose New Member's Plan" },
      { name: "Add Member Details" },
    ];
    if (!skipAddingPlans) {
      steps.push({ name: "Review" });
    }
    return steps;
  }, [skipAddingPlans]);

  useEffect(() => {
    if (!account || !isFocused) {
      return;
    }
    initPlanPackages(account);
  }, []);

  useEffect(() => {
    if (!selectedPlanPackage) {
      return;
    }
    refetchSignupCharge(selectedPlanPackage);
  }, [selectedPlanPackage, selectedBillingInterval, newMembers]);

  useEffect(() => {
    navigation.addListener("beforeRemove", () => {
      dispatch(resetBuyFlow());
    });
  }, []);

  if (!account || !planPackages) return null;

  if (loading)
    return (
      <FullScreenSpinner text="Adding membership to your account, please wait..."></FullScreenSpinner>
    );

  return (
    <MainContainer title="Add Membership" goBackAction={goBack} grayBackground>
      <View style={{ padding: 15 }}>
        <Stepper step={step} steps={availableSteps}>
          <StepperContent>
            <StepOne planPackages={planPackages} />
          </StepperContent>
          <StepperContent>
            <StepTwo onSubmit={handleSubmit} />
          </StepperContent>
          <StepperContent>
            {signupCharge && selectedPlanPackage && (
              <StepThree onSubmit={handleSubmit} />
            )}
          </StepperContent>
        </Stepper>
      </View>
    </MainContainer>
  );
};

export default AddMembership;
